import { nanoid } from "nanoid";
import { z } from "zod";
import { canonizar, sha256Hex } from "./canonico.js";
import { esCodigoValido } from "./codigos.js";

/**
 * El documento por bloques (fase 3): lo que el editor produce, lo que se firma y lo que la
 * plantilla imprime. Las plantillas por tipo de documento (qué secciones trae cada uno) van en
 * `./plantillas.ts`.
 *
 * Es JSON de ProseMirror tal cual lo emite TipTap, no un modelo propio. Guardar *eso*, validado
 * por este esquema (que solo admite los nodos que existen), evita una capa de traducción en los
 * dos sentidos, que es donde nacen los bugs de "lo que veo no es lo que se firmó" (ADR-03,
 * ADR-04). Los nodos estándar conservan su nombre de TipTap (`paragraph`, `heading`, `table`…)
 * y los propios van en español (`seccion`, `pasos`, `paso`, `imagen`, `flujograma`, `nota`,
 * `referencia`).
 *
 * Todo nodo de bloque lleva `attrs.id` (ADR-07): asignado al crearse y conservado al mover,
 * editar o copiar. Es lo que permite comparar revisiones y anclar marcas de revisión (fase 4).
 *
 * Determinismo (ADR-04): dos documentos iguales tienen que dar el mismo hash. Por eso acá no hay
 * `undefined`, no hay fechas ni objetos raros, y `hashDeContenido` pasa por `canonizar`.
 */

// ---------------------------------------------------------------------------
// Identificadores
// ---------------------------------------------------------------------------

/** 12 caracteres de nanoid: 71 bits, de sobra para bloques dentro de un catálogo. */
export const LARGO_ID_BLOQUE = 12;

export function nuevoIdDeBloque(): string {
  return nanoid(LARGO_ID_BLOQUE);
}

/** Un id de bloque: 12 caracteres de nanoid. */
export const esquemaIdDeBloque = z
  .string()
  .regex(/^[A-Za-z0-9_-]{12}$/, "Identificador de bloque inválido");
const id = esquemaIdDeBloque;

// ---------------------------------------------------------------------------
// Texto con formato (nodos en línea)
// ---------------------------------------------------------------------------

/** Solo tres marcas: un instructivo no necesita más, y menos formato es menos ambigüedad. */
const MARCAS = ["bold", "italic", "underline"] as const;
export type Marca = (typeof MARCAS)[number];

const texto = z.object({
  type: z.literal("text"),
  text: z.string().min(1),
  marks: z.array(z.object({ type: z.enum(MARCAS) })).optional(),
});

/** Cita a otro documento controlado, por código; se valida contra la base al guardar. */
const referencia = z.object({
  type: z.literal("referencia"),
  attrs: z.object({
    id,
    codigo: z.string().refine(esCodigoValido, "El código tiene la forma PROCESO-TIPO-NNN"),
  }),
});

const enLinea = z.discriminatedUnion("type", [texto, referencia]);
export type NodoEnLinea = z.infer<typeof enLinea>;

// ---------------------------------------------------------------------------
// Bloques
// ---------------------------------------------------------------------------

const parrafo = z.object({
  type: z.literal("paragraph"),
  attrs: z.object({ id }),
  content: z.array(enLinea).optional(),
});

/** Nivel 2 y 3 nada más: el 1 es el título de la sección, que lo pone la plantilla. */
const titulo = z.object({
  type: z.literal("heading"),
  attrs: z.object({ id, level: z.union([z.literal(2), z.literal(3)]) }),
  content: z.array(enLinea).optional(),
});

const item = z.object({
  type: z.literal("listItem"),
  attrs: z.object({ id }),
  content: z.array(parrafo).min(1),
});

const lista = z.object({
  type: z.literal("bulletList"),
  attrs: z.object({ id }),
  content: z.array(item).min(1),
});

/**
 * El bloque que distingue a un instructivo: cada paso lleva quién lo ejecuta. Se numera solo;
 * el número no se guarda, así reordenar no cambia el contenido de cada paso.
 */
const paso = z.object({
  type: z.literal("paso"),
  attrs: z.object({ id, responsable: z.string().max(120) }),
  content: z.array(parrafo).min(1),
});

const pasos = z.object({
  type: z.literal("pasos"),
  attrs: z.object({ id }),
  content: z.array(paso).min(1),
});

/** Las celdas son las de TipTap: `colspan`, `rowspan` y `colwidth` con sus valores por defecto. */
const atributosDeCelda = z.object({
  id,
  colspan: z.number().int().min(1).default(1),
  rowspan: z.number().int().min(1).default(1),
  colwidth: z.array(z.number()).nullable().default(null),
});
const celda = z.object({
  type: z.literal("tableCell"),
  attrs: atributosDeCelda,
  content: z.array(parrafo).min(1),
});
const celdaDeEncabezado = z.object({
  type: z.literal("tableHeader"),
  attrs: atributosDeCelda,
  content: z.array(parrafo).min(1),
});
const fila = z.object({
  type: z.literal("tableRow"),
  attrs: z.object({ id }),
  content: z.array(z.discriminatedUnion("type", [celda, celdaDeEncabezado])).min(1),
});
/**
 * Variantes de tabla, para copiar los formatos hechos en Excel:
 * - "firmas": sin bordes, cada celda es una línea de firma con el cargo y su código debajo (los
 *   renglones vacíos de arriba son el espacio para firmar con el lápiz).
 * - "sinBordes": texto en columnas sin rejilla (el bloque de fecha, frecuencia y leyenda).
 * Sin variante, la tabla es la de siempre; el atributo solo va cuando está puesto, por el hash.
 */
const VARIANTES_DE_TABLA = ["firmas", "sinBordes"] as const;
const tabla = z.object({
  type: z.literal("table"),
  attrs: z.object({ id, variante: z.enum(VARIANTES_DE_TABLA).nullable().optional() }),
  content: z.array(fila).min(1),
});

/** La imagen es un archivo ya subido al almacén; acá solo va su id. */
const imagen = z.object({
  type: z.literal("imagen"),
  attrs: z.object({
    id,
    archivoId: z.uuid(),
    alt: z.string().max(300).default(""),
    /** Ancho en porcentaje del cuerpo; 100 = todo el ancho. */
    ancho: z.number().int().min(20).max(100).default(100),
  }),
});

/** Código Mermaid. Se renderiza a SVG con la misma librería en pantalla y en el PDF. */
const flujograma = z.object({
  type: z.literal("flujograma"),
  attrs: z.object({ id, codigo: z.string().max(20_000) }),
});

export const TONOS_NOTA = ["nota", "advertencia"] as const;
const nota = z.object({
  type: z.literal("nota"),
  attrs: z.object({ id, tono: z.enum(TONOS_NOTA) }),
  content: z.array(parrafo).min(1),
});

/**
 * Fase 3b: la lista de verificación de un formato (GES-REG-022): áreas con sus puntos a revisar.
 * En el formato es la cuadrícula en blanco con las casillas N/A · Aceptable · No aceptable; en
 * cada hoja llena (tabla `hojas`) se marcan a mano, con la tinta (`./tinta.ts`).
 */
const areaDeVerificacion = z.object({
  nombre: z.string().trim().min(1).max(120),
  puntos: z.array(z.string().trim().min(1).max(120)).min(1).max(60),
});
const listaDeVerificacion = z.object({
  type: z.literal("listaDeVerificacion"),
  attrs: z.object({ id, areas: z.array(areaDeVerificacion).min(1).max(30) }),
});

const bloque = z.discriminatedUnion("type", [
  parrafo,
  titulo,
  lista,
  pasos,
  tabla,
  imagen,
  flujograma,
  nota,
  listaDeVerificacion,
]);
export type Bloque = z.infer<typeof bloque>;

// ---------------------------------------------------------------------------
// Secciones y documento
// ---------------------------------------------------------------------------

/**
 * Una sección numerada de la hoja ISO. `clave` la ata a la plantilla del tipo (`objetivo`,
 * `alcance`…) para saber cuáles obligatorias están; una sección agregada a mano va sin clave.
 * El número no se guarda: lo da el orden.
 */
const seccion = z.object({
  type: z.literal("seccion"),
  attrs: z.object({
    id,
    titulo: z.string().trim().min(1, "La sección necesita un título").max(120),
    clave: z.string().nullable().default(null),
  }),
  content: z.array(bloque).default([]),
});
export type Seccion = z.infer<typeof seccion>;

/**
 * Fase 3b: la hoja en horizontal (carta apaisada), para los formatos anchos como GES-REG-022.
 * Es parte del contenido (y por eso de lo que se firma) porque cambia cómo se ve el documento.
 * Sin `attrs`, o con `null`, la hoja es vertical: así siguen iguales los contenidos de antes.
 */
const ORIENTACIONES = ["vertical", "horizontal"] as const;
type Orientacion = (typeof ORIENTACIONES)[number];

/**
 * La letra compacta: para los formatos que en papel caben en una sola hoja con letra chica,
 * hechos en Excel, como GES-REG-001. La hoja normal va a 12 pt como los documentos de SMG; la
 * compacta baja la letra, los márgenes y los rellenos de las celdas para que el mismo contenido
 * quepa en una carta. Va en el contenido, como la orientación, porque cambia cómo se ve.
 */
const DENSIDADES = ["normal", "compacta"] as const;
type Densidad = (typeof DENSIDADES)[number];

export const esquemaContenido = z.object({
  type: z.literal("doc"),
  attrs: z
    .object({
      orientacion: z.enum(ORIENTACIONES).nullable().optional(),
      densidad: z.enum(DENSIDADES).nullable().optional(),
    })
    .optional(),
  content: z.array(seccion).min(1, "El documento necesita al menos una sección"),
});
export type Contenido = z.infer<typeof esquemaContenido>;

// ---------------------------------------------------------------------------
// Validación y consultas
// ---------------------------------------------------------------------------

export type Problema = { ruta: string; mensaje: string };

/**
 * Valida la forma (esquema) y lo que el esquema no puede: que ningún id se repita. Un id repetido
 * rompería la comparación de revisiones y el anclaje de marcas (ADR-07).
 */
export function validarContenido(
  valor: unknown,
): { ok: true; contenido: Contenido } | { ok: false; problemas: Problema[] } {
  const r = esquemaContenido.safeParse(valor);
  if (!r.success) {
    return {
      ok: false,
      problemas: r.error.issues.map((i) => ({ ruta: i.path.join("."), mensaje: i.message })),
    };
  }
  const vistos = new Set<string>();
  const repetidos: Problema[] = [];
  for (const { id: idBloque, ruta } of bloquesDe(r.data)) {
    if (vistos.has(idBloque))
      repetidos.push({ ruta, mensaje: `Id de bloque repetido: ${idBloque}` });
    vistos.add(idBloque);
  }
  return repetidos.length > 0
    ? { ok: false, problemas: repetidos }
    : { ok: true, contenido: r.data };
}

/**
 * Cualquier nodo del documento, visto por su forma y no por su tipo exacto: para los recorridos
 * que no distinguen bloques (buscar por id, listar las tablas, sacar el texto). El `| undefined`
 * explícito es por `exactOptionalPropertyTypes`: los tipos de Zod lo llevan así.
 */
export type Nodo = {
  type: string;
  attrs?: Record<string, unknown> | undefined;
  content?: Nodo[] | undefined;
  text?: string | undefined;
};

/** Los nodos de un tipo dado (`table`, `tableCell`…), en orden de documento. */
export function* nodosDeTipo(nodo: Nodo, tipo: string): Generator<Nodo> {
  if (nodo.type === tipo) yield nodo;
  for (const hijo of nodo.content ?? []) yield* nodosDeTipo(hijo, tipo);
}

/**
 * Todos los nodos con id, en orden de documento, con su ruta. Es la lista sobre la que la
 * fase 4 hace el diff por bloques: agregado, modificado, eliminado o movido.
 */
export function bloquesDe(contenido: Contenido): { id: string; tipo: string; ruta: string }[] {
  const lista: { id: string; tipo: string; ruta: string }[] = [];
  const recorrer = (nodo: Nodo, ruta: string): void => {
    const idBloque = nodo.attrs?.id;
    if (typeof idBloque === "string") lista.push({ id: idBloque, tipo: nodo.type, ruta });
    (nodo.content ?? []).forEach((hijo, i) => {
      recorrer(hijo, `${ruta}.content.${i}`);
    });
  };
  recorrer(contenido, "$");
  return lista;
}

/** SHA-256 del JSON canónico: lo que se firma (ADR-04). */
export function hashDeContenido(contenido: Contenido): Promise<string> {
  return sha256Hex(canonizar(contenido));
}

export function orientacionDe(contenido: Contenido): Orientacion {
  return contenido.attrs?.orientacion ?? "vertical";
}

export function densidadDe(contenido: Contenido): Densidad {
  return contenido.attrs?.densidad ?? "normal";
}

/** El texto de un nodo y sus hijos, sin formato, con un espacio entre bloques. */
export function textoDe(nodo: Nodo): string {
  if (nodo.type === "text") return nodo.text ?? "";
  if (nodo.type === "referencia") return String(nodo.attrs?.codigo ?? "");
  const partes = (nodo.content ?? []).map(textoDe).filter((t) => t.length > 0);
  // Dentro de un párrafo los tramos se pegan; entre bloques va un espacio.
  return nodo.type === "paragraph" || nodo.type === "heading" ? partes.join("") : partes.join(" ");
}

/**
 * Todo el texto del documento en una sola cadena, para la búsqueda por contenido (ADR-09):
 * títulos de sección, párrafos, pasos con su responsable, celdas y notas. Sin el código Mermaid,
 * que no es texto que alguien busque.
 */
export function textoPlano(contenido: Contenido): string {
  const lineas: string[] = [];
  for (const s of contenido.content) {
    lineas.push(s.attrs.titulo);
    for (const b of s.content) {
      if (b.type === "flujograma") continue;
      if (b.type === "imagen") {
        if (b.attrs.alt) lineas.push(b.attrs.alt);
        continue;
      }
      if (b.type === "pasos") {
        for (const p of b.content) {
          const t = textoDe(p);
          lineas.push(p.attrs.responsable ? `${t} — ${p.attrs.responsable}` : t);
        }
        continue;
      }
      if (b.type === "listaDeVerificacion") {
        for (const a of b.attrs.areas) lineas.push(`${a.nombre}: ${a.puntos.join(", ")}`);
        continue;
      }
      const t = textoDe(b);
      if (t.trim()) lineas.push(t);
    }
  }
  return lineas.join("\n");
}

/** Los archivos (imágenes) que el contenido cita: la API comprueba que sean de este documento. */
export function archivosDe(contenido: Contenido): string[] {
  const ids = new Set<string>();
  for (const s of contenido.content) {
    for (const b of s.content) if (b.type === "imagen") ids.add(b.attrs.archivoId);
  }
  return [...ids];
}

/** Los flujogramas del contenido, con su código Mermaid, para dibujarlos. */
export function flujogramasDe(contenido: Contenido): { id: string; codigo: string }[] {
  const lista: { id: string; codigo: string }[] = [];
  for (const s of contenido.content) {
    for (const b of s.content) {
      if (b.type === "flujograma") lista.push({ id: b.attrs.id, codigo: b.attrs.codigo });
    }
  }
  return lista;
}

/** Los códigos de otros documentos que este cita: la API comprueba que existan. */
export function referenciasDe(contenido: Contenido): string[] {
  const codigos = new Set<string>();
  const recorrer = (nodo: Nodo) => {
    if (nodo.type === "referencia") codigos.add(String(nodo.attrs?.codigo));
    nodo.content?.forEach(recorrer);
  };
  recorrer(contenido);
  return [...codigos];
}
